// src/features/works/components/PeopleSection.jsx
import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Button, Row, Col, Modal } from 'react-bootstrap';
import {  FaUser, FaIdCard, FaPeopleArrows  } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
// Componentes
import TableWrapper from '../../../components/TableWrapper';
import FilterSelect from '../../../components/FilterSelect';
import AssignPersonsToWork from './AssignPersonsToWork';

// Hooks
import { useColumnFilter } from '../../../hooks/useColumnFilter';

/**
 * Sección para gestionar las personas asignadas a una obra
 */
const PeopleSection = ({ workId, persons = [], onDataChange, showSnackbar, isWorkFinalized = false }) => {
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(false); // ✅ Estado para bloquear acciones
  const navigate = useNavigate();
  const [filteredPersons, setFilteredPersons] = useState(persons);

  // Value getter para documento
  const documentValueGetter = useCallback((person) => {
    return person.document || '';
  }, []);

  // Configuración de filtros
  const { 
    filterOptions: nameOptions, 
    activeFilters: activeNames,
    handleOptionsUpdate: handleNameOptionsUpdate,
    handleFiltersChange: handleNameChange,
    filterData: filterByName 
  } = useColumnFilter(persons, 'firstName', null, `work-${workId}-persons`);

  const { 
    filterOptions: documentOptions, 
    activeFilters: activeDocuments,
    handleOptionsUpdate: handleDocumentOptionsUpdate,
    handleFiltersChange: handleDocumentChange,
    filterData: filterByDocument 
  } = useColumnFilter(persons, 'document', documentValueGetter, `work-${workId}-persons`);

    // Funciones de manejo para acciones de tabla
    const handleViewPerson = useCallback((person) => {
      if (actionLoading) return; // ✅ Bloqueo para evitar múltiples clics
      navigate(`/people/${person.id}`);
    }, [navigate, actionLoading]);

  // Actualizar personas filtradas cuando cambian los datos o filtros
  useEffect(() => {
    let result = [...persons];
    
    // Aplicar filtros en cadena
    if (activeNames && activeNames.length > 0) {
      result = filterByName(result);
    }
    
    if (activeDocuments && activeDocuments.length > 0) {
      result = filterByDocument(result);
    }
    
    setFilteredPersons(result);
  }, [persons, activeNames, activeDocuments, filterByName, filterByDocument]);

  // Columnas para la tabla de personas
  const personColumns = useMemo(() => [
    { 
      headerName: 'Nombre', 
      field: 'firstName', 
      flex: 1,
      filter: 'agTextColumnFilter',
      floatingFilter: true
    },
    { 
      headerName: 'Apellido', 
      field: 'surname', 
      flex: 1,
      filter: 'agTextColumnFilter',
      floatingFilter: true
    },
    { 
      headerName: 'Documento', 
      field: 'document', 
      width: 130,
      filter: 'agTextColumnFilter',
      floatingFilter: true
    },
    { 
      headerName: 'Teléfono', 
      field: 'phone', 
      width: 130,
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      valueFormatter: params => params.value ? params.value.toString().padStart(9, '0') : ''
    }
  ], []);

  // Funciones para modal
  const handleOpenAssignModal = () => {
    setShowAssignModal(true);
  };

  const handleCloseAssignModal = () => {
    setShowAssignModal(false);
  };

  const handlePersonsAssigned = () => {
    setShowAssignModal(false);
    
    if (showSnackbar) {
      showSnackbar('success', 'Personas asignadas correctamente');
    }
    
    if (onDataChange) {
      onDataChange();
    }
  };

  return (
    <div className="d-flex flex-column h-100">
      {/* Fila de filtros y botón */}
      <Row className="mb-3">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            {/* Filtros dropdown */}
            <div className="d-flex gap-2">
              <FilterSelect
                options={nameOptions}
                activeFilters={activeNames}
                onFiltersChange={handleNameChange}
                onOptionsUpdate={handleNameOptionsUpdate}
                title="Filtrar por nombre"
                filterKey={`work-${workId}-persons-names`}
                icon={<FaUser />}
                variant="outline-secondary"
              />
              
              <FilterSelect
                options={documentOptions}
                activeFilters={activeDocuments}
                onFiltersChange={handleDocumentChange}
                onOptionsUpdate={handleDocumentOptionsUpdate}
                title="Filtrar por documento"
                filterKey={`work-${workId}-persons-documents`}
                icon={<FaIdCard />}
                variant="outline-secondary"
              />
            </div>
            
            {/* Botón asignar personas */}
            <Button 
              variant="primary"
              size="sm"
              className="d-flex align-items-center"
              onClick={handleOpenAssignModal}
              disabled={isWorkFinalized}
            >
              <FaPeopleArrows className="me-1" /> Modificar Personas
            </Button>
          </div>
        </Col>
      </Row>
      
      {/* Tabla */}
      <div className="flex-grow-1" style={{ height: 'calc(100vh - 280px)' }}>
        <TableWrapper
          columnDefs={personColumns}
          rowData={filteredPersons}
          tableType="personas"
            onView={handleViewPerson}
          customGridOptions={{
            pagination: true,
            paginationPageSize: 50,
            domLayout: 'autoHeight'
          }}
        />
      </div>
      
      {/* Modal para asignar personas */}
      <Modal 
        show={showAssignModal} 
        onHide={handleCloseAssignModal}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title className='text-center w-100'>Asignar Personas a la Obra</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {showAssignModal && (
            <AssignPersonsToWork 
              workId={workId}
              currentPersons={persons}
              onSave={handlePersonsAssigned}
              onCancel={handleCloseAssignModal}
            />
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default PeopleSection;